import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Cable,
  ChevronsLeft,
  ChevronsRight,
  Cpu,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import { useMe } from '../api/hooks.js';
import {
  clearSession,
  getSessionToken,
  loginUrl,
  shouldPromptOnboarding,
} from '../lib/auth.js';
import { cycleTheme, readTheme, type Theme } from '../lib/theme.js';
import { AppSwitcher } from './AppSwitcher.js';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/connections', label: 'My Connections', icon: Cable },
  { to: '/models', label: 'AI Models', icon: Cpu },
  { to: '/connect', label: 'AI Config', icon: KeyRound },
  { to: '/onboarding', label: 'Get Started', icon: BookOpen },
];

export function Layout() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [theme, setTheme] = useState<Theme>(readTheme);
  const token = getSessionToken();
  const publicPath = location.pathname === '/onboarding';

  useEffect(() => {
    document.title = 'Loom';
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = (mobile: boolean) => {
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    apply(mq.matches);
    const handler = (event: MediaQueryListEvent) => apply(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!token) return;
    if (location.pathname !== '/') return;
    if (shouldPromptOnboarding(token)) navigate('/onboarding', { replace: true });
  }, [token, location.pathname, navigate]);

  if (me.isLoading && token) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="space-y-2" style={{ width: 220 }}>
          <div
            className="h-3 animate-pulse rounded"
            style={{ background: 'var(--color-surface-3)', width: '70%' }}
          />
          <div
            className="h-3 animate-pulse rounded"
            style={{ background: 'var(--color-surface-3)', width: '50%' }}
          />
        </div>
      </div>
    );
  }

  if ((!token || me.isError || !me.data) && !publicPath) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div
          className="w-full max-w-sm rounded-lg p-7 text-center"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-line)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <img src="/favicon.svg" alt="" width={40} height={40} className="mx-auto rounded-lg" />
          <h1 className="mt-3 text-base font-semibold">Loom</h1>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
            Unified MCP portal — weaves Naton tools into one endpoint.
          </p>
          <a
            href={loginUrl()}
            className="mt-5 inline-block w-full rounded-md px-4 py-2 text-sm font-medium text-white"
            style={{ background: 'var(--color-accent)' }}
          >
            Sign in with Authentik
          </a>
          <a
            href="/onboarding"
            className="mt-3 inline-block text-sm text-[var(--color-accent)]"
          >
            New here? Get started guide
          </a>
        </div>
      </div>
    );
  }

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const closeOnMobile = () => {
    if (window.innerWidth < 768) setSidebarOpen(false);
  };
  const name = me.data?.name || me.data?.email || 'Guest';

  return (
    <div className="flex h-full overflow-hidden bg-void">
      {isMobile && sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={[
          'z-50 flex shrink-0 flex-col overflow-hidden transition-[width,opacity] duration-200',
          sidebarOpen ? 'opacity-100' : 'w-0 opacity-0',
          isMobile && sidebarOpen ? 'fixed inset-y-0 left-0' : 'relative',
        ].join(' ')}
        style={{
          width: sidebarOpen ? (isMobile ? 280 : 240) : 0,
          background: 'var(--color-chassis)',
          transitionTimingFunction: 'var(--ease-out-nd)',
        }}
      >
        <div className="flex h-full flex-col" style={{ width: isMobile ? 280 : 240 }}>
          <div className="group/header flex h-11 items-center justify-between px-3">
            <div className="flex min-w-0 items-center gap-1">
              <AppSwitcher />
              <div className="flex min-w-0 items-center gap-2 px-1.5 py-1 text-sm font-semibold">
                <img src="/favicon.svg" alt="" className="h-4 w-4 rounded" />
                <span className="truncate">Loom</span>
              </div>
            </div>
            <button
              type="button"
              className="rounded p-1 text-[var(--color-ink-faint)] opacity-0 transition-opacity group-hover/header:opacity-100"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          </div>

          <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 pt-1">
            {NAV.map((item) => (
              <Item key={item.to} {...item} onNavigate={closeOnMobile} />
            ))}
          </nav>

          <div className="space-y-0.5 border-t border-[var(--color-line)] p-2">
            {me.data ? (
              <Item to="/settings" label="Settings" icon={Settings} onNavigate={closeOnMobile} />
            ) : null}
            <button
              type="button"
              onClick={() => setTheme(cycleTheme(theme))}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-3)]"
              aria-label={`Theme: ${theme}`}
            >
              <ThemeIcon className="h-4 w-4" />
              <span className="capitalize">{theme} mode</span>
            </button>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <div
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}
              >
                {name.charAt(0).toUpperCase()}
              </div>
              <span
                className="min-w-0 flex-1 truncate text-xs text-[var(--color-ink-muted)]"
                title={me.data?.email ?? undefined}
              >
                {name}
              </span>
              {me.data ? (
                <button
                  type="button"
                  className="rounded p-1 text-[var(--color-ink-muted)] opacity-60 transition-opacity hover:opacity-100"
                  aria-label="Sign out"
                  onClick={() => {
                    clearSession();
                    window.location.href = '/';
                  }}
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              ) : (
                <a href={loginUrl()} className="text-xs text-[var(--color-accent)]">
                  Sign in
                </a>
              )}
            </div>
          </div>
        </div>
      </aside>

      {!sidebarOpen && !isMobile && (
        <div
          className="group fixed top-0 bottom-0 left-0 z-30 flex w-8 items-start pt-3 pl-1"
          onMouseEnter={() => setSidebarOpen(true)}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="rounded p-1 text-[var(--color-ink-faint)] opacity-0 transition-opacity group-hover:opacity-100"
            aria-label="Open sidebar"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="sticky top-0 z-10 flex h-11 shrink-0 items-center gap-2 px-3 backdrop-blur-sm"
          style={{ background: 'color-mix(in srgb, var(--color-surface) 80%, transparent)' }}
        >
          {isMobile && !sidebarOpen && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="rounded-md p-1.5 text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-3)]"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </header>
        <main className="min-h-0 flex-1 overflow-auto bg-void floor-grid">
          <div className="mx-auto max-w-5xl px-5 pb-16">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function Item({
  to,
  label,
  icon: Icon,
  end,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'flex items-center gap-2 rounded-sm px-2 py-1.5 font-mono text-[10px] transition-colors',
          isActive
            ? 'bg-raised font-medium text-ink'
            : 'text-ink-dim hover:bg-raised',
        ].join(' ')
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
    </NavLink>
  );
}
